import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { BiSolidCheckCircle, BiSolidXCircle } from 'react-icons/bi';
import { useAuth } from '@/auth/AuthContext';
import { ACCOUNTS_WRITE_ROLES, hasRole } from '@/auth/roles';
import { createPlan, getAccount, getPlan, listPlans, updateAccount } from '@/api/accounts.api';
import { ApiError } from '@/api/errors';
import { Banner } from '@/components/common/Banner';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { StatusChip } from '@/components/common/StatusChip';
import { Stepper } from '@/components/form/Stepper';
import { InstallmentCard } from '@/pages/accounts/InstallmentCard';
import { DownPaymentCard } from '@/pages/accounts/DownPaymentCard';
import { showErrorToast, showSuccessToast } from '@/lib/toast';
import { formatCurrencyEGP, formatDate, formatDateTime } from '@/lib/format';
import { accountProgress, toCents, toneForAccountProgress } from '@/lib/accountStatus';
import type { AccountResponse, PaymentPlanSummary, UpdateAccountRequest } from '@/types/shared';
import './AccountsDetailPage.css';

interface PlanDraftRow {
  installmentNo: number;
  amount: string;
  dueDate: string;
}

interface PlanDraft {
  totalAgreedFees: string;
  rows: PlanDraftRow[];
}

function toPlanDraft(plan: AccountResponse): PlanDraft {
  return {
    totalAgreedFees: plan.totalAgreedFees.toFixed(2),
    rows: [...plan.installments]
      .sort((a, b) => a.installmentNo - b.installmentNo)
      .map((i) => ({
        installmentNo: i.installmentNo,
        amount: i.amount.toFixed(2),
        dueDate: i.dueDate.slice(0, 10),
      })),
  };
}

function isPlanDirty(draft: PlanDraft, plan: AccountResponse): boolean {
  const baseline = toPlanDraft(plan);
  if (draft.totalAgreedFees !== baseline.totalAgreedFees) return true;
  if (draft.rows.length !== baseline.rows.length) return true;
  return draft.rows.some((row, idx) => {
    const base = baseline.rows[idx];
    return (
      !base ||
      row.installmentNo !== base.installmentNo ||
      row.amount !== base.amount ||
      row.dueDate !== base.dueDate
    );
  });
}

/** One month after `iso` (UTC, date-only) — mirrors the server's own next-due-date derivation for
 * newly-added unpaid slots (accounts.service.ts `buildAutoTarget`). */
function nextMonthIso(iso: string | undefined): string {
  const base = iso ? new Date(`${iso}T00:00:00.000Z`) : new Date();
  if (Number.isNaN(base.getTime())) return new Date().toISOString().slice(0, 10);
  base.setUTCMonth(base.getUTCMonth() + 1);
  return base.toISOString().slice(0, 10);
}

export function AccountsDetailPage() {
  const { t, i18n } = useTranslation();
  const { studentCode } = useParams<{ studentCode: string }>();
  const { user } = useAuth();
  const canEdit = hasRole(user?.role, ACCOUNTS_WRITE_ROLES);

  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  /** The student's true Active plan (11-accounts-multi-plan.md §5.1) — always the edit target and
   * the source of truth for the "Add plan" completed-gate, regardless of which plan is currently
   * being viewed below. */
  const [activePlan, setActivePlan] = useState<AccountResponse | null>(null);
  /** The plan currently rendered in the summary/fees/installments sections — equals `activePlan`
   * by default; switching the plan-history dropdown to an Archived plan loads it here, read-only. */
  const [viewedPlan, setViewedPlan] = useState<AccountResponse | null>(null);
  const [selectedPlanNumber, setSelectedPlanNumber] = useState<number | null>(null);

  const [planHistory, setPlanHistory] = useState<PaymentPlanSummary[] | null>(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyError, setHistoryError] = useState<string | null>(null);

  const [planSwitchLoading, setPlanSwitchLoading] = useState(false);
  const [planSwitchError, setPlanSwitchError] = useState<string | null>(null);

  const [planDraft, setPlanDraft] = useState<PlanDraft | null>(null);
  const [planSaving, setPlanSaving] = useState(false);
  const [planBanner, setPlanBanner] = useState<{ tone: 'success' | 'danger'; text: string } | null>(
    null,
  );

  const [addPlanConfirmOpen, setAddPlanConfirmOpen] = useState(false);
  const [addPlanSubmitting, setAddPlanSubmitting] = useState(false);

  const refreshHistory = useCallback(
    (code: string) => {
      setHistoryLoading(true);
      setHistoryError(null);
      listPlans(code)
        .then((res) => setPlanHistory(res.data))
        .catch(() => setHistoryError(t('accounts.detail.plans.historyLoadError')))
        .finally(() => setHistoryLoading(false));
    },
    [t],
  );

  useEffect(() => {
    if (!studentCode) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getAccount(studentCode)
      .then((data) => {
        if (cancelled) return;
        setActivePlan(data);
        setViewedPlan(data);
        setSelectedPlanNumber(data.planNumber);
        setPlanDraft(toPlanDraft(data));
      })
      .catch((err) => {
        if (cancelled) return;
        const apiError = ApiError.fromUnknown(err);
        setLoadError(
          apiError.code === 'NOT_FOUND'
            ? t('accounts.detail.notFound')
            : t('accounts.detail.loadError'),
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [studentCode, t]);

  useEffect(() => {
    if (!studentCode) return;
    refreshHistory(studentCode);
  }, [studentCode, refreshHistory]);

  const isViewingActive = viewedPlan?.status === 'Active';
  const canEditPlan = canEdit && isViewingActive;

  const paidCount = useMemo(
    () => viewedPlan?.installments.filter((i) => i.status === 'Paid').length ?? 0,
    [viewedPlan],
  );

  const planDirty = useMemo(() => {
    if (!viewedPlan || !planDraft) return false;
    return isPlanDirty(planDraft, viewedPlan);
  }, [viewedPlan, planDraft]);

  const totalFeesCents = planDraft ? toCents(parseFloat(planDraft.totalAgreedFees) || 0) : 0;
  const planSumCents = planDraft
    ? planDraft.rows.reduce((sum, row) => sum + toCents(parseFloat(row.amount) || 0), 0)
    : 0;
  const planMatches = planDraft ? totalFeesCents === planSumCents : true;
  const totalFeesValid = planDraft ? parseFloat(planDraft.totalAgreedFees) > 0 : false;
  const allAmountsValid = planDraft
    ? planDraft.rows.every(
        (row) => !Number.isNaN(parseFloat(row.amount)) && parseFloat(row.amount) > 0,
      )
    : false;
  const allDueDatesValid = planDraft ? planDraft.rows.every((row) => Boolean(row.dueDate)) : false;

  const canSavePlan =
    canEditPlan &&
    planDirty &&
    planMatches &&
    totalFeesValid &&
    allAmountsValid &&
    allDueDatesValid &&
    !planSaving;

  function handleTotalFeesChange(value: string) {
    setPlanBanner(null);
    setPlanDraft((prev) => (prev ? { ...prev, totalAgreedFees: value } : prev));
  }

  function handleRowAmountChange(installmentNo: number, value: string) {
    setPlanBanner(null);
    setPlanDraft((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((row) =>
              row.installmentNo === installmentNo ? { ...row, amount: value } : row,
            ),
          }
        : prev,
    );
  }

  function handleRowDueDateChange(installmentNo: number, value: string) {
    setPlanBanner(null);
    setPlanDraft((prev) =>
      prev
        ? {
            ...prev,
            rows: prev.rows.map((row) =>
              row.installmentNo === installmentNo ? { ...row, dueDate: value } : row,
            ),
          }
        : prev,
    );
  }

  function handleInstallmentCountChange(next: number | '') {
    if (next === '') return;
    setPlanBanner(null);
    setPlanDraft((prev) => {
      if (!prev) return prev;
      const target = Math.max(next, paidCount, 1);
      if (target === prev.rows.length) return prev;

      if (target > prev.rows.length) {
        const existingNos = new Set(prev.rows.map((r) => r.installmentNo));
        const rows = [...prev.rows];
        let lastDue = rows[rows.length - 1]?.dueDate;
        let candidate = 1;
        while (rows.length < target) {
          while (existingNos.has(candidate)) candidate += 1;
          const dueDate = nextMonthIso(lastDue);
          rows.push({ installmentNo: candidate, amount: '0.00', dueDate });
          existingNos.add(candidate);
          lastDue = dueDate;
        }
        return { ...prev, rows: rows.sort((a, b) => a.installmentNo - b.installmentNo) };
      }

      const lockedNos = new Set(
        (viewedPlan?.installments ?? [])
          .filter((i) => i.status === 'Paid')
          .map((i) => i.installmentNo),
      );
      const removable = prev.rows
        .filter((r) => !lockedNos.has(r.installmentNo))
        .sort((a, b) => b.installmentNo - a.installmentNo);
      const removeCount = prev.rows.length - target;
      const removeNos = new Set(removable.slice(0, removeCount).map((r) => r.installmentNo));
      return { ...prev, rows: prev.rows.filter((r) => !removeNos.has(r.installmentNo)) };
    });
  }

  function handleAutoBalance() {
    setPlanBanner(null);
    setPlanDraft((prev) => {
      if (!prev || !viewedPlan) return prev;
      const lockedNos = new Set(
        viewedPlan.installments.filter((i) => i.status === 'Paid').map((i) => i.installmentNo),
      );
      const totalCents = toCents(parseFloat(prev.totalAgreedFees) || 0);
      const lockedCents = prev.rows
        .filter((r) => lockedNos.has(r.installmentNo))
        .reduce((sum, r) => sum + toCents(parseFloat(r.amount) || 0), 0);
      const unlockedRows = prev.rows.filter((r) => !lockedNos.has(r.installmentNo));
      if (unlockedRows.length === 0) return prev;
      const remainderCents = Math.max(0, totalCents - lockedCents);
      const base = Math.floor(remainderCents / unlockedRows.length);
      const amounts = new Array(unlockedRows.length).fill(base) as number[];
      amounts[amounts.length - 1] += remainderCents - base * unlockedRows.length;
      let idx = 0;
      const rows = prev.rows.map((row) => {
        if (lockedNos.has(row.installmentNo)) return row;
        const cents = amounts[idx];
        idx += 1;
        return { ...row, amount: (cents / 100).toFixed(2) };
      });
      return { ...prev, rows };
    });
  }

  function handleResetPlan() {
    if (!viewedPlan) return;
    setPlanDraft(toPlanDraft(viewedPlan));
    setPlanBanner(null);
  }

  async function handleSavePlan() {
    if (!viewedPlan || !planDraft || !studentCode || !canSavePlan) return;

    setPlanSaving(true);
    setPlanBanner(null);
    try {
      const payload: UpdateAccountRequest = {
        totalAgreedFees: Number(planDraft.totalAgreedFees),
        installmentCount: planDraft.rows.length,
        installments: planDraft.rows.map((row) => ({
          installmentNo: row.installmentNo,
          amount: Number(row.amount),
          dueDate: row.dueDate,
        })),
      };
      const updated = await updateAccount(studentCode, payload);
      setActivePlan(updated);
      setViewedPlan(updated);
      setPlanDraft(toPlanDraft(updated));
      const message = t('accounts.detail.planSaveSuccess');
      setPlanBanner({ tone: 'success', text: message });
      showSuccessToast(message);
      refreshHistory(studentCode);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      if (apiError.code === 'NETWORK_ERROR') {
        const message = t('accounts.detail.networkError');
        setPlanBanner({ tone: 'danger', text: message });
        showErrorToast(message);
      } else if (apiError.code === 'PLAN_ARCHIVED_READONLY') {
        setPlanBanner({ tone: 'danger', text: t('accounts.detail.plans.archivedReadonlyError') });
      } else {
        setPlanBanner({
          tone: 'danger',
          text: apiError.message || t('accounts.detail.planSaveError'),
        });
      }
    } finally {
      setPlanSaving(false);
    }
  }

  function handleInstallmentSaved(updated: AccountResponse, savedNo: number) {
    setActivePlan(updated);
    setViewedPlan((prev) => {
      if (!prev) return updated;
      const updatedRow = updated.installments.find((i) => i.installmentNo === savedNo);
      const installments = prev.installments.map((inst) =>
        inst.installmentNo === savedNo && updatedRow ? updatedRow : inst,
      );
      return { ...updated, installments };
    });
    if (studentCode) refreshHistory(studentCode);
  }

  function handleSelectPlan(planNumber: number) {
    setSelectedPlanNumber(planNumber);
    setPlanSwitchError(null);
    setPlanBanner(null);
    if (activePlan && planNumber === activePlan.planNumber) {
      setViewedPlan(activePlan);
      setPlanDraft(toPlanDraft(activePlan));
      return;
    }
    if (!studentCode) return;
    setPlanSwitchLoading(true);
    getPlan(studentCode, planNumber)
      .then((data) => {
        setViewedPlan(data);
        setPlanDraft(toPlanDraft(data));
      })
      .catch((err) => {
        const apiError = ApiError.fromUnknown(err);
        setPlanSwitchError(apiError.message || t('accounts.detail.plans.loadPlanError'));
      })
      .finally(() => setPlanSwitchLoading(false));
  }

  async function handleAddPlan() {
    if (!studentCode || !activePlan) return;
    setAddPlanSubmitting(true);
    setPlanBanner(null);
    try {
      const created = await createPlan(studentCode);
      setActivePlan(created);
      setViewedPlan(created);
      setSelectedPlanNumber(created.planNumber);
      setPlanDraft(toPlanDraft(created));
      setAddPlanConfirmOpen(false);
      const message = t('accounts.detail.plans.addPlanSuccess', { no: created.planNumber });
      setPlanBanner({
        tone: 'success',
        text: message,
      });
      showSuccessToast(message);
      refreshHistory(studentCode);
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      setAddPlanConfirmOpen(false);
      if (apiError.code === 'NETWORK_ERROR') {
        const message = t('accounts.detail.networkError');
        setPlanBanner({ tone: 'danger', text: message });
        showErrorToast(message);
      } else if (apiError.code === 'ACTIVE_PLAN_NOT_COMPLETED') {
        setPlanBanner({
          tone: 'danger',
          text: t('accounts.detail.plans.activePlanNotCompletedError'),
        });
      } else {
        setPlanBanner({
          tone: 'danger',
          text: apiError.message || t('accounts.detail.plans.addPlanError'),
        });
      }
    } finally {
      setAddPlanSubmitting(false);
    }
  }

  if (loading) {
    return <p className="fa-form-loading">{t('common.loading')}</p>;
  }

  if (loadError || !activePlan || !viewedPlan || !planDraft) {
    return (
      <div>
        <Banner tone="danger" assertive>
          {loadError ?? t('accounts.detail.notFound')}
        </Banner>
        <Link to="/accounts" className="fa-back-link">
          <span className="fa-back-link__arrow" aria-hidden="true" />
          {t('accounts.detail.backToList')}
        </Link>
      </div>
    );
  }

  const { student } = viewedPlan;
  const progress = accountProgress(viewedPlan.totalPaid, viewedPlan.totalAgreedFees);
  const diffCents = Math.abs(totalFeesCents - planSumCents);
  const addPlanDisabled = !activePlan.isCompleted || addPlanSubmitting;

  return (
    <section className="fa-accounts-detail">
      <Link to="/accounts" className="fa-back-link">
        <span className="fa-back-link__arrow" aria-hidden="true" />
        {t('accounts.detail.backToList')}
      </Link>

      <header className="fa-accounts-detail__header">
        <div className="fa-accounts-detail__heading">
          <div>
            <h1 className="fa-accounts-detail__title">
              <span className="fa-ltr-isolate">{student.studentCode}</span>
              {' — '}
              {student.nameAr}
            </h1>
            <p className="fa-accounts-detail__subtitle">{student.nameEn}</p>
            <div className="fa-accounts-detail__badges">
              <StatusChip
                label={t(`accounts.detail.progress.${progress}`)}
                tone={toneForAccountProgress(progress)}
              />
              {!canEdit && (
                <span className="fa-chip fa-chip--neutral">
                  {t('accounts.detail.readOnlyBadge')}
                </span>
              )}
            </div>
          </div>
        </div>
        <Link to={`/students/${student.studentCode}`} className="fa-btn fa-btn--secondary">
          {t('accounts.detail.viewStudent')}
        </Link>
      </header>

      <section className="fa-form-section">
        <h2 className="fa-form-section__title">{t('accounts.detail.summaryTitle')}</h2>
        <dl className="fa-stat-grid">
          <div className="fa-stat-card">
            <dt className="fa-stat-card__label">{t('accounts.detail.totalAgreedFees')}</dt>
            <dd className="fa-stat-card__value">
              <bdi>{formatCurrencyEGP(viewedPlan.totalAgreedFees, i18n.language)}</bdi>
            </dd>
          </div>
          <div className="fa-stat-card">
            <dt className="fa-stat-card__label">{t('accounts.detail.totalPaid')}</dt>
            <dd className="fa-stat-card__value">
              <bdi>{formatCurrencyEGP(viewedPlan.totalPaid, i18n.language)}</bdi>
            </dd>
          </div>
          <div className="fa-stat-card">
            <dt className="fa-stat-card__label">{t('accounts.detail.totalRemaining')}</dt>
            <dd className="fa-stat-card__value">
              <bdi>{formatCurrencyEGP(viewedPlan.totalRemaining, i18n.language)}</bdi>
            </dd>
          </div>
          <div className="fa-stat-card">
            <dt className="fa-stat-card__label">{t('accounts.detail.lastPaymentDate')}</dt>
            <dd className="fa-stat-card__value">
              {viewedPlan.lastPaymentDate ? (
                <bdi>{formatDate(viewedPlan.lastPaymentDate, i18n.language)}</bdi>
              ) : (
                <span className="fa-stat-card__muted">{t('accounts.detail.lastPaymentNone')}</span>
              )}
            </dd>
          </div>
        </dl>
      </section>

      <section className="fa-form-section">
        {/* One-time-per-student down payment — student-level, not tied to any single plan. */}
        <DownPaymentCard studentCode={student.studentCode} canEdit={canEdit} />
      </section>

      <section className="fa-form-section fa-accounts-detail__plans-section">
        <div className="fa-accounts-detail__plans-header">
          <h2 className="fa-form-section__title">{t('accounts.detail.plans.sectionTitle')}</h2>
          {canEdit && (
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              disabled={addPlanDisabled}
              title={
                !activePlan.isCompleted ? t('accounts.detail.plans.addPlanDisabledHint') : undefined
              }
              onClick={() => setAddPlanConfirmOpen(true)}
            >
              {t('accounts.detail.plans.addPlan')}
            </button>
          )}
        </div>

        {canEdit && !activePlan.isCompleted && (
          <p className="fa-field__hint">{t('accounts.detail.plans.addPlanDisabledHint')}</p>
        )}

        {planBanner && (
          <Banner tone={planBanner.tone} assertive={planBanner.tone === 'danger'}>
            {planBanner.text}
          </Banner>
        )}

        <div className="fa-accounts-detail__plan-meta">
          <span className="fa-chip fa-chip--neutral fa-accounts-detail__plan-number">
            <bdi>{t('accounts.detail.plans.planNumber', { no: viewedPlan.planNumber })}</bdi>
          </span>
          <StatusChip
            label={t(`accounts.detail.plans.status.${viewedPlan.status}`)}
            tone={viewedPlan.status === 'Active' ? 'success' : 'neutral'}
          />
          {viewedPlan.isCompleted && (
            <StatusChip label={t('accounts.detail.plans.completed')} tone="info" />
          )}
          <span className="fa-accounts-detail__plan-dates">
            {t('accounts.detail.plans.activatedOn')}{' '}
            <bdi>{formatDate(viewedPlan.activatedAt, i18n.language)}</bdi>
            {viewedPlan.archivedAt && (
              <>
                {' · '}
                {t('accounts.detail.plans.archivedOn')}{' '}
                <bdi>{formatDate(viewedPlan.archivedAt, i18n.language)}</bdi>
              </>
            )}
          </span>
        </div>

        <div className="fa-field fa-accounts-detail__plan-switcher">
          <label htmlFor="planSwitcher">{t('accounts.detail.plans.switcherLabel')}</label>
          <select
            id="planSwitcher"
            value={selectedPlanNumber ?? ''}
            disabled={
              historyLoading || planSwitchLoading || !planHistory || planHistory.length <= 1
            }
            onChange={(e) => handleSelectPlan(Number(e.target.value))}
          >
            {(planHistory ?? []).map((p) => (
              <option key={p.planNumber} value={p.planNumber}>
                {t('accounts.detail.plans.optionLabel', {
                  no: p.planNumber,
                  status: t(`accounts.detail.plans.status.${p.status}`),
                  fees: formatCurrencyEGP(p.totalAgreedFees, i18n.language),
                  paid: formatCurrencyEGP(p.totalPaid, i18n.language),
                  remaining: formatCurrencyEGP(p.totalRemaining, i18n.language),
                })}
              </option>
            ))}
          </select>
          {historyError && (
            <p className="fa-field__error" role="alert">
              {historyError}
            </p>
          )}
          {planSwitchError && (
            <p className="fa-field__error" role="alert">
              {planSwitchError}
            </p>
          )}
          {planSwitchLoading && <p className="fa-field__hint">{t('common.loading')}</p>}
        </div>

        {!isViewingActive && (
          <Banner tone="info">
            {t('accounts.detail.plans.archivedReadOnlyNotice', { no: viewedPlan.planNumber })}
          </Banner>
        )}
      </section>

      <section className="fa-form-section" aria-busy={planSwitchLoading}>
        <h2 className="fa-form-section__title">{t('accounts.detail.planTitle')}</h2>

        <div className="fa-field-row">
          <div className="fa-field">
            <label htmlFor="totalAgreedFees">{t('accounts.detail.totalAgreedFees')}</label>
            <div className="fa-accounts-detail__fees-input">
              <input
                id="totalAgreedFees"
                type="number"
                min="0.01"
                step="0.01"
                className="fa-ltr-isolate"
                value={planDraft.totalAgreedFees}
                disabled={!canEditPlan || planSaving}
                onChange={(e) => handleTotalFeesChange(e.target.value)}
              />
              <span className="fa-accounts-detail__currency-suffix">{viewedPlan.currency}</span>
            </div>
          </div>
          {isViewingActive ? (
            <div className="fa-field">
              <span className="fa-field__label" id="installmentCount-label">
                {t('accounts.detail.installmentCount')}
              </span>
              <Stepper
                id="installmentCount"
                value={planDraft.rows.length}
                min={Math.max(paidCount, 1)}
                onChange={handleInstallmentCountChange}
                decrementLabel={t('accounts.detail.decreaseInstallments')}
                incrementLabel={t('accounts.detail.increaseInstallments')}
                disabled={!canEditPlan}
              />
              {paidCount > 0 && (
                <p className="fa-field__hint">
                  {t('accounts.detail.cannotReduceBelowPaid', { count: paidCount })}
                </p>
              )}
            </div>
          ) : (
            <div className="fa-field">
              <span className="fa-field__label">{t('accounts.detail.installmentCount')}</span>
              <p className="fa-accounts-detail__readonly-value">
                <bdi>{viewedPlan.installmentCount}</bdi>
              </p>
            </div>
          )}
          {canEditPlan && (
            <div className="fa-field fa-accounts-detail__auto-balance">
              <span className="fa-field__label fa-visually-hidden">
                {t('accounts.detail.autoBalance')}
              </span>
              <button
                type="button"
                className="fa-btn fa-btn--secondary"
                disabled={planSaving}
                onClick={handleAutoBalance}
              >
                {t('accounts.detail.autoBalance')}
              </button>
              <p className="fa-field__hint">{t('accounts.detail.autoBalanceHint')}</p>
            </div>
          )}
        </div>

        <div
          className={`fa-accounts-detail__plan-status fa-accounts-detail__plan-status--${
            planMatches ? 'match' : 'mismatch'
          }`}
          role="status"
          aria-live="polite"
        >
          <span className="fa-accounts-detail__plan-status-icon" aria-hidden="true">
            {planMatches ? <BiSolidCheckCircle /> : <BiSolidXCircle />}
          </span>
          <span>
            {planMatches
              ? t('accounts.detail.planMatchHint', {
                  sum: formatCurrencyEGP(planSumCents / 100, i18n.language),
                })
              : t('accounts.detail.planMismatchHint', {
                  sum: formatCurrencyEGP(planSumCents / 100, i18n.language),
                  fees: formatCurrencyEGP(totalFeesCents / 100, i18n.language),
                  diff: formatCurrencyEGP(diffCents / 100, i18n.language),
                })}
          </span>
        </div>

        <div className="fa-accounts-detail__installments">
          {planDraft.rows.map((row) => {
            const canonical = viewedPlan.installments.find(
              (i) => i.installmentNo === row.installmentNo,
            );
            return (
              <InstallmentCard
                key={row.installmentNo}
                studentCode={student.studentCode}
                installmentNo={row.installmentNo}
                amount={row.amount}
                dueDate={row.dueDate}
                canonical={canonical}
                canEdit={canEditPlan}
                planSaving={planSaving}
                onAmountChange={(value) => handleRowAmountChange(row.installmentNo, value)}
                onDueDateChange={(value) => handleRowDueDateChange(row.installmentNo, value)}
                onPaymentSaved={(updated) => handleInstallmentSaved(updated, row.installmentNo)}
              />
            );
          })}
        </div>

        {canEditPlan && (
          <div className="fa-accounts-detail__plan-actions">
            <button
              type="button"
              className="fa-btn fa-btn--ghost"
              disabled={!planDirty || planSaving}
              onClick={handleResetPlan}
            >
              {t('accounts.detail.resetPlan')}
            </button>
            <button
              type="button"
              className="fa-btn fa-btn--primary"
              disabled={!canSavePlan}
              title={!planMatches ? t('accounts.detail.planSaveDisabledHint') : undefined}
              onClick={handleSavePlan}
            >
              {planSaving ? t('students.form.submitting') : t('accounts.detail.savePlan')}
            </button>
          </div>
        )}
      </section>

      <section className="fa-form-section fa-accounts-detail__audit">
        <h2 className="fa-form-section__title">{t('accounts.detail.auditTitle')}</h2>
        <div className="fa-accounts-detail__audit-grid">
          <p>
            <span className="fa-accounts-detail__audit-label">{t('accounts.detail.addedBy')}</span>
            <span>
              {viewedPlan.createdBy?.fullName ?? t('accounts.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(viewedPlan.createdAt, i18n.language)}</bdi>
            </span>
          </p>
          <p>
            <span className="fa-accounts-detail__audit-label">
              {t('accounts.detail.updatedBy')}
            </span>
            <span>
              {viewedPlan.updatedBy?.fullName ?? t('accounts.unknownUser')}
              {' — '}
              <bdi>{formatDateTime(viewedPlan.updatedAt, i18n.language)}</bdi>
            </span>
          </p>
        </div>
      </section>

      <ConfirmDialog
        open={addPlanConfirmOpen}
        title={t('accounts.detail.plans.addPlanConfirmTitle')}
        description={t('accounts.detail.plans.addPlanConfirmBody', {
          no: activePlan.planNumber,
        })}
        confirmLabel={t('accounts.detail.plans.addPlanConfirmAction')}
        cancelLabel={t('students.form.cancel')}
        confirmDisabled={addPlanSubmitting}
        onConfirm={handleAddPlan}
        onCancel={() => setAddPlanConfirmOpen(false)}
      />
    </section>
  );
}
